import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, Avatar, Collapse, Paper, Divider, IconButton, TextField, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowDown2, ArrowUp2, Link21, Messages2, Send2 } from 'iconsax-react';
import { replyToThread } from '../../services/task.service';
import { Task, ThreadMessage } from '../../types/task.type';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { WORK_STATUS_COLORS, dueHintParts, getWorkStatus, workStatusLabelKey } from '../../utils/task.utils';
import TaskRowActions from './TaskRowActions';

export const ORG_TASK_GRID = '1fr minmax(220px, 1.4fr) 150px 110px 100px minmax(108px, auto)';

export interface TaskRowProps {
  task: Task;
  actionsLoading?: boolean;
  onToggleComplete: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  trailingActions?: React.ReactNode;
  beforeActions?: React.ReactNode;
  gridColumns?: string;
  onThreadReply?: () => void | Promise<void>;
}

export default function TaskRow({
  task,
  actionsLoading,
  onToggleComplete,
  onEdit,
  onDelete,
  trailingActions,
  beforeActions,
  gridColumns = ORG_TASK_GRID,
  onThreadReply,
}: TaskRowProps) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [threadExpanded, setThreadExpanded] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [localMessages, setLocalMessages] = useState<ThreadMessage[]>([]);
  const workStatus = getWorkStatus(task);
  const overdue = task.is_overdue && workStatus !== 'completed';
  const statusBg = WORK_STATUS_COLORS[workStatus];
  const hint = dueHintParts(task);
  const dueHint = hint ? t(hint.key, { count: hint.count }) : null;
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  const hasMessages = (task.thread_messages?.length ?? 0) > 0;
  const canReply = !!task.thread_name;
  const hasThread = hasMessages || canReply || !!task.thread_link;

  useEffect(() => {
    if (task.thread_messages) {
      setLocalMessages(task.thread_messages);
    }
  }, [task.thread_messages]);

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpenInChat = () => {
    if (task.thread_link) {
      window.open(task.thread_link, '_blank');
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !task.thread_name || sending) return;

    setSending(true);
    try {
      const newMessage = await replyToThread({
        google_task_id: task.google_task_id,
        task_list_id: task.task_list_id,
        employee_id: task.employee_id,
        thread_name: task.thread_name,
        text: replyText.trim(),
      });
      setLocalMessages(prev => [...prev, newMessage]);
      setReplyText('');
      showToast(t('tasks.thread.replySent') || 'Reply sent', TOAST_TYPES.SUCCESS);
      await onThreadReply?.();
    } catch {
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          alignItems: 'center', px: 2.5, py: 1.5, gap: 2,
          borderBottom: hasThread && !threadExpanded ? '1px solid #F3F4F6' : 'none',
          transition: 'background 0.12s',
          '&:hover': { bgcolor: '#F9FAFB' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {task.title}
            </Typography>
            {hasThread && (
              <IconButton
                size="small"
                onClick={() => setThreadExpanded(!threadExpanded)}
                sx={{ p: 0.25, ml: 0.5 }}
              >
                {threadExpanded ? <ArrowUp2 size={14} color="#3B82F6" /> : <ArrowDown2 size={14} color="#3B82F6" />}
              </IconButton>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            {task.task_list_name || '—'}
          </Typography>
          {(task.linked_objectives?.length ?? 0) > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {task.linked_objectives.map(o => (
                <Chip
                  key={o.id}
                  icon={<Link21 size={11} color="#4338CA" />}
                  label={o.title}
                  size="small"
                  sx={{
                    height: 20,
                    maxWidth: '100%',
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    border: '1px solid #C7D2FE',
                    borderRadius: '6px',
                    '& .MuiChip-icon': { ml: '6px', mr: '-2px' },
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.68rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.space_name || '—'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', fontWeight: 600, background: 'linear-gradient(135deg, #3B82F6, #4F46E5)' }}>
            {(task.employee_name || '?').charAt(0)}
          </Avatar>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.employee_name || '—'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Calendar size={13} color={overdue ? '#EF4444' : '#94A3B8'} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: overdue ? '#EF4444' : 'text.secondary', fontWeight: overdue ? 600 : 400 }}>
              {task.due ? new Date(task.due).toLocaleDateString(locale, { month: 'short', day: 'numeric' }) : '—'}
            </Typography>
            {dueHint && (
              <Typography sx={{ fontSize: '0.7rem', color: overdue ? '#EF4444' : 'text.secondary', lineHeight: 1.2 }}>
                {dueHint}
              </Typography>
            )}
          </Box>
        </Box>

        <Chip
          label={t(workStatusLabelKey(workStatus))}
          size="small"
          sx={{ bgcolor: statusBg.bg, color: statusBg.color, fontWeight: 500, fontSize: '0.75rem', border: 'none', height: 24 }}
        />

        {beforeActions}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
          <TaskRowActions
            task={task}
            loading={actionsLoading}
            onToggleComplete={onToggleComplete}
            onEdit={onEdit}
            onDelete={onDelete}
          />
          {trailingActions}
        </Box>
      </Box>

      {hasThread && (
        <Collapse in={threadExpanded}>
          <Box sx={{ px: 2.5, pb: 1.5, borderBottom: '1px solid #F3F4F6' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              pb: 1,
              borderBottom: '1px solid #F3F4F6',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Messages2 size={16} color="#3B82F6" />
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151' }}>
                  {t('tasks.thread.title')}
                </Typography>
                <Chip
                  label={localMessages.length}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: '#DBEAFE',
                    color: '#2563EB',
                    border: 'none',
                  }}
                />
              </Box>
              {task.thread_link && (
                <IconButton size="small" onClick={handleOpenInChat} sx={{ p: 0.5 }}>
                  <Link21 size={16} color="#3B82F6" />
                </IconButton>
              )}
            </Box>

            {hasMessages && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: '#FAFBFC',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                maxHeight: '300px',
                overflowY: 'auto',
                mb: 1.5,
              }}
            >
              {localMessages.map((msg, index) => (
                <Box key={msg.message_id}>
                  {index > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar
                      src={msg.sender_avatar_url || undefined}
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                        bgcolor: msg.sender_type === 'BOT' ? '#EEF2FF' : '#DBEAFE',
                        color: msg.sender_type === 'BOT' ? '#6366F1' : '#2563EB',
                      }}
                    >
                      {!msg.sender_avatar_url && msg.sender_name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827' }}>
                          {msg.sender_name}
                        </Typography>
                        {msg.sender_type === 'BOT' && (
                          <Chip
                            label="BOT"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: '#DBEAFE',
                              color: '#2563EB',
                              border: 'none',
                            }}
                          />
                        )}
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                          {formatTime(msg.create_time)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          color: '#374151',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
            )}

            {canReply && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: hasMessages ? 1.5 : 0 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={3}
                placeholder={t('tasks.thread.replyPlaceholder') || 'اكتب رداً...'}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                  },
                }}
              />
              <IconButton
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
                sx={{
                  bgcolor: '#3B82F6',
                  color: '#fff',
                  '&:hover': { bgcolor: '#2563EB' },
                  '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : <Send2 size={20} color="#fff" variant="Bold" />}
              </IconButton>
            </Box>
            )}
          </Box>
        </Collapse>
      )}
    </>
  );
}
